import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, errMsg } from "../lib/api";
import { listLocal, removeLocal } from "../lib/offline";
import { Badge, Btn, Money, PageHeader, Select, Th, Td, Empty, useNames } from "../components/common";
import { useI18n } from "../lib/i18n";

const STATUSES = ["", "draft", "submitted", "pending_verification", "verified", "approved", "rejected", "finalized"];

export default function Collections() {
  const { user, meta, can, offline } = useAuth();
  const { t } = useI18n();
  const { gName, uName } = useNames(meta);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [err, setErr] = useState("");
  const [local, setLocal] = useState(listLocal(user.id));
  useEffect(() => {
    if (offline) return;
    api.get("/collections", { params: { status: status || undefined } }).then((r) => setItems(r.data)).catch((e) => setErr(errMsg(e)));
  }, [status, offline]);
  return (
    <div>
      <PageHeader title={t("my_reports")} subtitle="Every collection report in your assigned scope, newest first."
        right={can("collection:write") && <Link to="/collections/new"><Btn data-testid="collections-new-button"><Plus className="h-5 w-5" /> {t("record_collection")}</Btn></Link>} />
      {local.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="local-drafts-list">
          {local.map((x) => (
            <div key={x.client_uuid} className="flex items-center justify-between gap-3 rounded-xl border-2 border-[#E9D5FF] bg-[#F3E8FF] p-3">
              <div><Badge s="local" /> <span className="ml-2 font-semibold">{x.payload.service_date}</span> <span className="text-sm text-[#6B21A8]">saved {x.saved_at.slice(0, 16).replace("T", " ")}</span>{x.error && <div className="text-sm text-[#B91C1C]">{x.error}</div>}</div>
              <button onClick={() => { removeLocal(user.id, x.client_uuid); setLocal(listLocal(user.id)); }} className="text-[#B91C1C]" aria-label="Remove local draft"><Trash2 /></button>
            </div>
          ))}
        </div>
      )}
      <div className="max-w-xs mb-4">
        <Select data-testid="collections-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUSES.map((s) => <option key={s} value={s}>{s ? t(`status_${s}`) : "All statuses"}</option>)}
        </Select>
      </div>
      {err && <p className="text-[#B91C1C] mb-4">{err}</p>}
      {items.length === 0 ? <Empty>No collection reports yet.</Empty> : (
        <div className="tbl-wrap">
          <table className="tbl" data-testid="collections-table">
            <thead><tr><Th>Reference</Th><Th>Date</Th><Th>Occasion</Th><Th>GKK / Unit</Th><Th>Status</Th><Th r>Total</Th></tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-[#FAF7F2]">
                  <Td><Link to={`/collections/${c.id}`} data-testid={`collection-row-${c.id}`} className="font-mono font-bold text-[#C85A32] hover:underline">{c.ref_no || c.draft_no}</Link></Td>
                  <Td>{c.service_date}</Td>
                  <Td>{c.occasion_name}{c.report_type !== "collection" && <div className="text-xs font-bold text-[#92400E]">{t(`rt_${c.report_type}`)}</div>}</Td>
                  <Td>{gName(c.gkk_id)}<div className="text-xs text-[#6E6259]">{uName(c.unit_id)}</div></Td>
                  <Td><Badge s={c.status} /></Td>
                  <Td r><Money c={c.total_cents} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
